import { DeliveryEnum } from '../interfaces'
import { ShipmentGroup } from '../interfaces/Import/ShipmentGroupInterface'
import {
  BusinessUnitLike,
  CityLike,
  DOMICILIO_LABEL,
  ServiceOptionLike,
  TIENDA_LABEL,
  cityDisplay,
  originOfficeDisplayByCode,
  sanitizeForExcel,
  serviceDisplayById,
  storeDisplayByCode,
} from './bulkTemplateDisplay'

/**
 * Mapper INVERSO del parser de carga masiva: `ShipmentGroup[]` → filas de la plantilla V4.
 *
 * Sirve para bajar en formato plantilla las guías que fallaron en una importación, de modo
 * que el operador las corrija en Excel y vuelva a subir el archivo, en vez de editarlas una
 * por una en la vista de corrección.
 *
 * Es el espejo del parser de `ShipmentBulkUpload.handleFileSubmit` (fila → grupo). Los dos
 * lados tienen que hablar el mismo idioma, así que todos los textos de display salen de
 * `bulkTemplateDisplay.ts`, que es también de donde los toma la hoja oculta `Listas` que
 * alimenta los desplegables.
 */

/** Claves de columna de la plantilla (mismo `key` que el array `cols` de exports.tsx). */
export type BulkTemplateColKey =
  | 'tracking'
  | 'remitTipoDoc'
  | 'remitNumDoc'
  | 'remitNombre'
  | 'oficinaOrigen'
  | 'servicio'
  | 'favSiNo'
  | 'favId'
  | 'destTipoDoc'
  | 'destNumDoc'
  | 'destNombre'
  | 'destTel'
  | 'destCorreo'
  | 'tipoEntrega'
  | 'tienda'
  | 'ciudad'
  | 'lat'
  | 'lng'
  | 'direccion'
  | 'puntoRef'
  | 'codPostal'
  | 'categoria'
  | 'codCategoria'
  | 'cantPiezas'
  | 'peso'
  | 'alto'
  | 'ancho'
  | 'largo'
  | 'valor'
  | 'observ'
  /** Columna informativa extra, solo en la plantilla prellenada de guías con error. */
  | 'motivoError'
  /**
   * Identificador interno de la línea de la carga masiva, en columna OCULTA.
   *
   * Es la clave con la que el archivo corregido se vuelve a emparejar con su guía al
   * subirlo. No se empareja por nº de guía aliada porque ese número es, muchas veces, el
   * error que el operador viene a corregir: si lo cambia, emparejar por él fallaría justo
   * en el caso que esta función existe para resolver.
   */
  | 'lineId'

/**
 * Una fila de datos de la plantilla. La semántica de cada clave es de TRES estados, y el
 * escritor de `exports.tsx` la respeta:
 *
 * - **clave ausente** → no se toca la celda. Conserva lo que puso la plantilla en blanco,
 *   que en varias columnas es una FÓRMULA de autocompletado.
 * - **`null`** → se vacía la celda (borra la fórmula que hubiera).
 * - **string / number** → literal.
 *
 * La distinción no es cosmética: para una guía a Tienda, las columnas Ciudad y Dirección
 * se dejan AUSENTES a propósito para que su fórmula (`VLOOKUP` sobre la tienda elegida)
 * siga viva y se recalcule sola cuando el operador corrija la tienda — que es justo lo que
 * vino a hacer con este archivo.
 */
export type BulkTemplateRow = Partial<
  Record<BulkTemplateColKey, string | number | null>
>

export interface BulkTemplateRowsContext {
  /** `state.inmutable.taxIdentificationTypes`. */
  identificationTypes: {
    taxIdentificationTypeId: string | number
    abbreviationName: string
  }[]
  /** `state.bulkCatalogs.cities`. */
  citiesType: CityLike[]
  /** Catálogo de tiendas DESTINO del contrato (el mismo que recibe la plantilla). */
  businessUnits: BusinessUnitLike[]
  /** `state.bulkCatalogs.pieceCategories`. */
  pieceCategories: { id: string | number; name?: string }[]
  /** Oficinas ORIGEN usables del contrato (`user.originBusinessUnits`). */
  originBusinessUnits?: BusinessUnitLike[]
  /** Servicios autorizados (`authorizedServiceOptions`). */
  serviceOptions?: ServiceOptionLike[]
}

/** Una guía a exportar: el grupo reconstruido + su nº de guía aliada + el motivo del error. */
export interface BulkTemplateRowInput {
  group: ShipmentGroup
  /**
   * Nº de guía aliada AUTORITATIVO — el del detalle de la importación, no solo el que
   * traiga el payload.
   *
   * Es la única columna cuya pérdida no deja rastro: el parser descarta enteras las filas
   * sin nº de guía aliada, así que una celda vacía acá borraría la guía del archivo sin
   * ningún error visible.
   */
  tracking: string
  /** Texto para la columna informativa "Motivo del error". */
  errorReason?: string
  /**
   * `shipment_bulk_upload_detail_id` de la línea, para la columna oculta de emparejado.
   * Sin esto el archivo corregido solo se puede emparejar por nº de guía aliada.
   */
  lineId?: string
}

/**
 * Abreviación del tipo de documento TAL CUAL está en el catálogo, con el guion final
 * ("V-"). Es lo que la plantilla carga en `Listas!E` y por tanto lo único que el
 * desplegable acepta. `buildShipmentList` sí recorta el guion para armar el payload; acá
 * NO se replica ese recorte o cada celda de tipo de documento saldría fuera de lista.
 */
const docTypeAbbreviation = (
  identificationTypes: BulkTemplateRowsContext['identificationTypes'],
  docTypeId?: string
): string => {
  if (!docTypeId) return ''
  const t = identificationTypes.find(
    (x) => String(x.taxIdentificationTypeId) === String(docTypeId)
  )
  return t?.abbreviationName || ''
}

/** Nombre de la categoría del catálogo; si no resuelve, el que traía la pieza. */
const categoryName = (
  pieceCategories: BulkTemplateRowsContext['pieceCategories'],
  categoryId?: string,
  fallback?: string
): string => {
  const c = pieceCategories.find(
    (x) => String(x.id ?? '') === String(categoryId ?? '')
  )
  return c?.name || fallback || ''
}

/**
 * Valor declarado por pieza redondeado al centavo.
 *
 * `payloadJsonToShipmentGroup` reparte el agregado del payload entre las piezas
 * (`amount / totalItems`), lo que produce decimales infinitos como 3.3333333333. La
 * columna de la plantilla es "por pieza", así que se redondea igual que lo haría el
 * operador al escribirla a mano.
 */
const roundMoney = (n: number): number => Math.round((n || 0) * 100) / 100

/** Coordenada como texto: la columna es `numFmt '@'` y el parser la lee con extractCoord. */
const coordText = (n: number | null | undefined): string =>
  n == null ? '' : String(n)

/**
 * Convierte guías a filas de la plantilla.
 *
 * UNA FILA POR PIEZA DISTINTA, todas con el mismo "Número de guía aliada": es como el
 * parser reconstruye la guía (agrupa por tracking y suma las piezas). Las columnas de
 * nivel guía se REPITEN en cada fila del mismo tracking — el parser arma el grupo desde la
 * PRIMERA fila y en las siguientes solo mira las piezas, así que repetirlas no cambia el
 * resultado; se repiten igual porque una fila de continuación SIN nº de guía la descarta
 * el filtro de filas vacías del parser, y porque un archivo con medias filas en blanco
 * parece corrupto y el operador lo "arregla".
 *
 * Una guía sin piezas igual produce una fila (con las columnas de pieza vacías): si se
 * omitiera, la guía desaparecería del archivo sin que nadie lo note.
 *
 * FIDELIDAD: el grupo viene de `payloadJsonToShipmentGroup`, que re-agrupa piezas
 * idénticas y reparte el valor declarado agregado. Las filas resultantes pueden no ser 1:1
 * con las que el operador subió y el valor por pieza es el promedio — el TOTAL sí se
 * conserva.
 */
export const shipmentGroupsToTemplateRows = (
  inputs: BulkTemplateRowInput[],
  ctx: BulkTemplateRowsContext
): BulkTemplateRow[] => {
  const {
    identificationTypes,
    citiesType,
    businessUnits,
    pieceCategories,
    originBusinessUnits = [],
    serviceOptions = [],
  } = ctx

  const rows: BulkTemplateRow[] = []

  for (const { group: g, tracking, errorReason, lineId } of inputs) {
    const isHome = g.deliveryType === DeliveryEnum.DELIVERY

    // Columnas de nivel guía, calculadas una vez y repetidas en cada fila de pieza.
    const guideCells: BulkTemplateRow = {
      tracking: sanitizeForExcel(tracking || ''),
      remitTipoDoc: docTypeAbbreviation(identificationTypes, g.shipperDocType),
      remitNumDoc: sanitizeForExcel(g.shipperDocNumber || ''),
      remitNombre: sanitizeForExcel(g.shipperName || ''),
      // Solo existen en la plantilla si el contrato tiene más de una opción; si la columna
      // no existe, el escritor descarta la clave sin ruido.
      oficinaOrigen: originOfficeDisplayByCode(
        originBusinessUnits,
        g.originStoreCode
      ),
      servicio: serviceDisplayById(serviceOptions, g.serviceId),
      // ¿Es favorito? y su identificador se dejan AUSENTES (celda intacta), no en "No".
      // El payload_json no transporta el favorito, así que no hay identificador que
      // escribir; y un "No" explícito el parser lo lee como `optedOutOfFavorite`, que
      // apaga el banner de sugerencia de RIF en el Review. En blanco el operador conserva
      // las dos opciones y el formato condicional sigue pintando la celda como toca.
      destTipoDoc: docTypeAbbreviation(identificationTypes, g.consigneeDocType),
      destNumDoc: sanitizeForExcel(g.consigneeDocNumber || ''),
      destNombre: sanitizeForExcel(g.consigneeName || ''),
      destTel: sanitizeForExcel(g.consigneePhone || ''),
      destCorreo: sanitizeForExcel(g.consigneeEmail || ''),
      tipoEntrega: isHome ? DOMICILIO_LABEL : TIENDA_LABEL,
      observ: sanitizeForExcel(g.notes || ''),
      ...(errorReason !== undefined
        ? { motivoError: sanitizeForExcel(errorReason) }
        : {}),
      ...(lineId ? { lineId } : {}),
    }

    if (isHome) {
      // Domicilio: la fórmula de Ciudad/Dirección evalúa a "" (solo autocompleta desde la
      // tienda o el favorito), así que taparía el dato. Van literales.
      guideCells.tienda = null
      guideCells.ciudad = cityDisplay(citiesType, g.destinationCity)
      guideCells.direccion = sanitizeForExcel(g.destinationAddress || '')
      guideCells.puntoRef = sanitizeForExcel(g.addressReference || '')
      guideCells.codPostal = sanitizeForExcel(g.postalCode || '')
      // Sin sanitizar: una latitud negativa quedaría con una comilla real adelante y
      // `extractCoord` no la podría leer al re-subir el archivo.
      guideCells.lat = coordText(g.latitude)
      guideCells.lng = coordText(g.longitude)
    } else {
      // Tienda: Ciudad y Dirección se dejan AUSENTES para conservar su fórmula, que las
      // deriva de la tienda elegida y se recalcula sola si el operador la corrige.
      guideCells.tienda = storeDisplayByCode(businessUnits, g.storeCode)
      guideCells.lat = null
      guideCells.lng = null
      guideCells.puntoRef = null
      guideCells.codPostal = null
    }

    if (!g.pieces || g.pieces.length === 0) {
      rows.push({ ...guideCells })
      continue
    }

    for (const p of g.pieces) {
      rows.push({
        ...guideCells,
        // Se escribe el NOMBRE de la categoría, no el código: "Código de categoría" es una
        // columna oculta con fórmula que lo deriva de acá. El mapper NUNCA emite
        // `codCategoria` — pisarla con un literal dejaría un código viejo cuando el
        // operador cambie la categoría, y el parser agrupa las piezas justamente por ese
        // código.
        categoria: categoryName(pieceCategories, p.categoryId, p.productName),
        cantPiezas: p.quantity || 1,
        peso: p.weight || 0,
        alto: p.height || 0,
        ancho: p.width || 0,
        largo: p.length || 0,
        valor: roundMoney(p.declaredValue),
      })
    }
  }

  return rows
}
